// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2026-05-30 16:27:17 +0200 (Sat, 30 May 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 163412 $
//  Purpose   : reducers for project mordor
// =============================================================================
// ---Third-party imports
// ---Framework imports
import { dict, mapItems, isArray } from 'ldash';
import { handleSubState, handleAction } from 'uix/actions';
// ---Local imports
import { sendFrame, clearInbookSearch, setInbookSearch, setI3F, setViewerGeometry, setFocusedViewerGeometry, setSelectedViewerGeometry, setModifiedViewerGeometry, clearFocusedViewerGeometry } from './actions';
import { imageRequestsHandlers, imageRequestsInitialState } from './imageload/reducers';
// ---Exports
let i3f = handleSubState('i3f', [
    handleAction(setI3F, ({ manifestById, ...i3f }, { payload })=>({
            ...i3f,
            manifestById: {
                ...manifestById,
                [payload.manifest]: payload.data
            }
        }), {
        manifestById: {}
    })
]);
let viewer = handleSubState('viewer', [
    handleAction(sendFrame, ({ viewerFrame, ...data }, { payload })=>({
            ...data,
            viewerFrame: {
                ...viewerFrame,
                [payload.viewerId]: payload
            }
        })),
    handleAction('GO', (data)=>({
            ...data,
            viewerFrame: {}
        })),
    handleAction(clearInbookSearch, (data, { payload })=>({
            ...data,
            activeInBookSearches: {
                ...data.activeInBookSearches,
                [payload.viewerId]: null
            }
        })),
    handleAction(setInbookSearch, (data, { payload })=>({
            ...data,
            activeInBookSearches: {
                ...data.activeInBookSearches,
                [payload.viewerId]: payload.searchData
            }
        })),
    ...imageRequestsHandlers,
    handleAction(setViewerGeometry, ({ geometry, ...viewer }, { payload })=>{
        let { viewerId: vid, geometry: new_geometry } = payload;
        if (!new_geometry) {
            // flush all otids for vid
            geometry = {
                ...geometry,
                [vid]: {}
            };
        } else {
            let prev_vid_geo = geometry[vid] || {};
            // keep prev geoms for supporting multiple geometry sources
            let geoms = dict(mapItems(new_geometry, (otid, geoms)=>[
                    otid,
                    {
                        ...prev_vid_geo[otid] ?? {},
                        ...geoms
                    }
                ]));
            geometry = {
                ...geometry,
                [vid]: geoms
            };
        }
        return {
            ...viewer,
            geometry
        };
    }),
    handleAction(setFocusedViewerGeometry, ({ focusedGeometry, ...viewer }, { payload })=>{
        let { viewerId, otid, geomtype, index } = payload;
        index = isArray(index) ? index : [
            index
        ];
        return {
            ...viewer,
            focusedGeometry: {
                ...focusedGeometry,
                [viewerId]: {
                    otid,
                    geomtype,
                    index
                }
            }
        };
    }),
    handleAction(setSelectedViewerGeometry, ({ selectedGeometry, ...viewer }, { payload })=>{
        let { viewerId, otid, geomtype, items, clearOther } = payload;
        let prev_otids = clearOther ? {} : selectedGeometry[viewerId] || {};
        let prev_geom = prev_otids[otid] || {};
        // full reset if no geomtype
        let next_selection = geomtype ? {
            ...prev_geom,
            [geomtype]: items
        } : {};
        return {
            ...viewer,
            selectedGeometry: {
                ...selectedGeometry,
                [viewerId]: {
                    ...prev_otids,
                    [otid]: next_selection
                }
            },
            // reset focus if selection changes
            focusedGeometry: {
                ...viewer.focusedGeometry,
                [viewerId]: null
            }
        };
    }),
    // reset stuff on view changes
    handleAction('ACTIVATE_VIEW', (viewer, { payload })=>({
            ...viewer,
            selectedGeometry: {},
            focusedGeometry: {},
            modifiedGeometry: {}
        })),
    handleAction(setModifiedViewerGeometry, ({ modifiedGeometry, ...viewer }, { payload })=>{
        let { viewerId, otid, geomtype, items, merge } = payload;
        let prev_geom = modifiedGeometry[viewerId] || {};
        let prev_items = merge ? prev_geom[geomtype] || {} : {};
        // reset if items === null else merge
        items = items ? {
            ...prev_items,
            ...items
        } : {};
        return {
            ...viewer,
            modifiedGeometry: {
                ...modifiedGeometry,
                [viewerId]: {
                    ...prev_geom,
                    [geomtype]: {
                        [otid]: items
                    }
                }
            }
        };
    }),
    handleAction(clearFocusedViewerGeometry, (viewer)=>({
            ...viewer,
            focusedGeometry: {}
        }))
], {
    geometryById: {},
    imageLocsById: {},
    viewerFrame: {},
    activeInBookSearches: {},
    inBookSearchById: {},
    ...imageRequestsInitialState,
    geometry: {},
    focusedGeometry: {},
    selectedGeometry: {},
    modifiedGeometry: {}
});
export const data = [
    i3f,
    viewer
];
